import { useQueryClient } from '@tanstack/react-query';
import {
  getGetStationTasksQueryKey,
  useBumpTask,
  useRecallTask,
  type StationTask,
  type StationTaskStatus,
  type StationView,
} from '@ordr-as/ordr-service-types/epos';

function nextStatusOnBump(status: StationTaskStatus | undefined): StationTaskStatus | undefined {
  if (status === 'QUEUED') return 'ONGOING';
  if (status === 'ONGOING' || status === 'RECALLED') return 'DONE';
  return undefined;
}

/**
 * Bump (advance) and recall (undo) with optimistic cache updates against the station's StationView.
 * The list stays a flat, patch-by-id shape, so a bump flips status in place and the board's client
 * ranking re-sorts for free (ADR 0004). The server response carries the authoritative status, which
 * corrects the optimistic guess in onSuccess; onSettled refetches the full truth.
 */
export function useKdsTaskMutations(locationId: number, stationId: number) {
  const queryClient = useQueryClient();
  const queryKey = getGetStationTasksQueryKey(locationId, stationId);

  const patchTask = (taskId: number, patch: (task: StationTask) => StationTask) => {
    queryClient.setQueryData<StationView>(
      queryKey,
      (current) =>
        current && { ...current, tasks: current.tasks.map((task) => (task.id === taskId ? patch(task) : task)) }
    );
  };

  const applyOptimisticStatus = async (
    taskId: number,
    toStatus: (task: StationTask) => StationTaskStatus | undefined
  ) => {
    await queryClient.cancelQueries({ queryKey });
    const previous = queryClient.getQueryData<StationView>(queryKey);
    patchTask(taskId, (task) => {
      const status = toStatus(task);
      return status ? { ...task, status, updatedUtc: new Date().toISOString() } : task;
    });
    return { previous };
  };

  const rollback = (context: { previous?: StationView } | undefined) => {
    if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
  };

  const reconcile = (taskId: number, status: StationTaskStatus | undefined) => {
    if (status) patchTask(taskId, (task) => ({ ...task, status }));
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const bumpMutation = useBumpTask({
    mutation: {
      onMutate: ({ taskId }) => applyOptimisticStatus(taskId, (task) => nextStatusOnBump(task.status)),
      onSuccess: (response, { taskId }) => reconcile(taskId, response.status as StationTaskStatus),
      onError: (_error, _vars, context) => rollback(context),
      onSettled: invalidate,
    },
  });

  const recallMutation = useRecallTask({
    mutation: {
      onMutate: ({ taskId }) => applyOptimisticStatus(taskId, () => 'ONGOING'),
      onSuccess: (response, { taskId }) => reconcile(taskId, response.status as StationTaskStatus),
      onError: (_error, _vars, context) => rollback(context),
      onSettled: invalidate,
    },
  });

  return {
    bump: (taskId: number) => bumpMutation.mutate({ locationId, taskId }),
    recall: (taskId: number) => recallMutation.mutate({ locationId, taskId }),
    pendingTaskIds: new Set(
      [
        bumpMutation.isPending ? bumpMutation.variables?.taskId : undefined,
        recallMutation.isPending ? recallMutation.variables?.taskId : undefined,
      ].filter((id): id is number => id !== undefined)
    ),
  };
}
